import {
  createTRPCProxyClient,
  httpBatchLink,
  httpLink,
  httpSubscriptionLink,
  splitLink,
} from '@trpc/client';
import { createTRPCNext } from '@trpc/next';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@web-app/server/routers/_app';
import { supportConciergeLinks } from '@web-app/utils/support-concierge/logging-link';

/**
 * Inferred input/output types for the app router. Lets client components type
 * tRPC data without importing server-only entities.
 */
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * Get base URL for tRPC requests
 */
function getBaseUrl() {
  if (globalThis.window !== undefined) {
    // Browser should use relative path
    return '';
  }

  // Fallback to localhost
  return `http://localhost:${process.env.PORT ?? 3000}`;
}

/**
 * Typed tRPC hooks for React components
 *
 * This client provides React hooks (`.useQuery()`, `.useMutation()`) that
 * integrate with React Query for automatic caching, refetching, and state management.
 *
 * Usage:
 *   const { data } = trpc.example.hello.useQuery({ text: 'tRPC' });
 *   const mutation = trpc.example.createGreeting.useMutation();
 */
export const trpc = createTRPCNext<AppRouter>({
  config() {
    return {
      links: [
        splitLink({
          condition: (operation) => operation.type === 'subscription',
          true: httpSubscriptionLink({ url: `${getBaseUrl()}/api/trpc` }),
          false: splitLink({
            // Support reads must not delay unrelated queries in a shared batch.
            condition: (operation) =>
              operation.path.startsWith('supportConcierge.'),
            true: supportConciergeLinks(`${getBaseUrl()}/api/trpc`),
            false: httpBatchLink({ url: `${getBaseUrl()}/api/trpc` }),
          }),
        }),
      ],
      /**
       * @link https://tanstack.com/query/v5/docs/reference/QueryClient
       */
      queryClientConfig: {
        defaultOptions: {
          queries: {
            // Align with existing React Query config
            retry: 3,
            retryDelay: 10 * 1000,
            staleTime: Infinity,
            refetchOnWindowFocus: false,
          },
        },
      },
    };
  },
  /**
   * @link https://trpc.io/docs/ssr
   */
  ssr: false,
});

/**
 * Direct tRPC proxy client for use outside React components
 *
 * This client is used in Jotai atoms and other non-React contexts where
 * you need to call tRPC endpoints directly (e.g., `.query()`, `.mutate()`)
 * rather than using React hooks.
 *
 * Usage in Jotai atoms:
 *   const result = await trpcClient.journalEntries.getByPatientId.query({ ... });
 *
 * Usage in other non-React contexts:
 *   const data = await trpcClient.example.hello.query({ text: 'tRPC' });
 */
export const trpcClient = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${getBaseUrl()}/api/trpc`,
      // Clerk handles authentication via cookies automatically
    }),
  ],
});

/**
 * Unbatched tRPC client for independent query execution.
 *
 * Unlike trpcClient which batches multiple queries into a single HTTP request,
 * this client sends each tRPC call as a separate HTTP request.
 *
 * Use when queries should load independently so failures don't impact each other.
 */
export const trpcClientUnbatched = createTRPCProxyClient<AppRouter>({
  links: [
    httpLink({
      url: `${getBaseUrl()}/api/trpc`,
    }),
  ],
});
